import React, { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Box, IconButton, Chip, TextField, Button, Typography, Collapse } from '@mui/material'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt'
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt'
import { useTheme } from '@mui/material/styles'
import { setMessageFeedback, submitMessageFeedback } from '/src/redux/aiJobSearch'

const FEEDBACK_REASONS = [
  { key: 'didnt_understand', label: "Didn't understand me" },
  { key: 'wrong_specialty', label: 'Wrong specialty' },
  { key: 'wrong_location', label: 'Wrong location' },
  { key: 'salary_too_low', label: 'Salary too low' },
  { key: 'irrelevant_jobs', label: 'Irrelevant jobs' },
  { key: 'missing_criteria', label: 'Missing key criteria' },
  { key: 'inaccurate', label: 'Inaccurate information' },
  { key: 'not_helpful', label: 'Not helpful' },
  { key: 'other', label: 'Other' },
]

const POSITIVE_FEEDBACK_REASONS = [
  { key: 'relevant_jobs', label: 'Relevant jobs' },
  { key: 'right_specialty', label: 'Right specialty' },
  { key: 'good_location', label: 'Good location' },
  { key: 'fair_salary', label: 'Fair salary' },
  { key: 'understood_needs', label: 'Understood my needs' },
  { key: 'helpful_response', label: 'Helpful response' },
  { key: 'other', label: 'Other' },
]

const MessageFeedback = ({ messageId, chatbotMessageId, feedback, conversationId }) => {
  const theme = useTheme()
  const dispatch = useDispatch()

  const [selectedReasons, setSelectedReasons] = useState(feedback?.reasons || [])
  const [comment, setComment] = useState(feedback?.comment || '')
  const [showThanks, setShowThanks] = useState(false)

  const thanksTimerRef = useRef(null)

  useEffect(() => {
    return () => {
      if (thanksTimerRef.current) {
        clearTimeout(thanksTimerRef.current)
      }
    }
  }, [])

  const vote = feedback?.vote || null
  const isSubmitted = !!feedback?.submittedAt
  const canSubmit = selectedReasons.length > 0 || comment.trim().length > 0

  const handleThumbUp = () => {
    if (vote === 'up') {
      // Clicking the same thumb again - just clear local state (don't submit)
      dispatch(setMessageFeedback({ messageId, vote: null }))
    } else {
      // New thumbs up - submit vote immediately to backend, keep panel open for detail
      dispatch(setMessageFeedback({
        messageId,
        vote: 'up',
        reasons: [],
        comment: '',
      }))
      submitMessageFeedback({
        chatbotMessageId,
        conversationId,
        vote: 'up',
        reasons: [],
        comment: '',
      })
      setSelectedReasons([])
      setComment('')
    }
  }

  const handleThumbDown = () => {
    if (vote === 'down') {
      // Clicking the same thumb again - just clear local state (don't submit)
      dispatch(setMessageFeedback({ messageId, vote: null }))
      setSelectedReasons([])
      setComment('')
    } else {
      // New thumbs down - submit vote immediately to backend, keep panel open for detail
      dispatch(setMessageFeedback({
        messageId,
        vote: 'down',
        reasons: [],
        comment: '',
      }))
      submitMessageFeedback({
        chatbotMessageId,
        conversationId,
        vote: 'down',
        reasons: [],
        comment: '',
      })
    }
  }

  const handleReasonToggle = (key) => {
    setSelectedReasons(prev =>
      prev.includes(key) ? prev.filter(r => r !== key) : [...prev, key]
    )
  }

  const handleSubmit = () => {
    // Update local state with the detailed feedback
    dispatch(setMessageFeedback({
      messageId,
      vote,
      reasons: selectedReasons,
      comment,
      submittedAt: new Date().toISOString(),
    }))

    // Submit to backend (will upsert existing feedback with reasons/comment)
    submitMessageFeedback({
      chatbotMessageId,
      conversationId,
      vote,
      reasons: selectedReasons,
      comment,
    })

    setShowThanks(true)
    thanksTimerRef.current = setTimeout(() => {
      setShowThanks(false)
    }, 2000)
  }

  return (
    <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <IconButton
          size="small"
          onClick={handleThumbUp}
          sx={{
            color: vote === 'up'
              ? theme.palette.ai.main
              : (vote === 'down' ? theme.palette.grey[300] : theme.palette.grey[400]),
            '&:hover': {
              color: vote === 'up' ? theme.palette.ai.main : theme.palette.grey[600],
            },
          }}
        >
          {vote === 'up'
            ? <ThumbUpIcon sx={{ fontSize: '0.875rem' }} />
            : <ThumbUpOffAltIcon sx={{ fontSize: '0.875rem' }} />
          }
        </IconButton>
        <IconButton
          size="small"
          onClick={handleThumbDown}
          sx={{
            color: vote === 'down'
              ? theme.palette.ai.main
              : (vote === 'up' ? theme.palette.grey[300] : theme.palette.grey[400]),
            '&:hover': {
              color: vote === 'down' ? theme.palette.ai.main : theme.palette.grey[600],
            },
          }}
        >
          {vote === 'down'
            ? <ThumbDownIcon sx={{ fontSize: '0.875rem' }} />
            : <ThumbDownOffAltIcon sx={{ fontSize: '0.875rem' }} />
          }
        </IconButton>
      </Box>

      <Collapse in={vote === 'up' && !isSubmitted} timeout={200}>
        <Box
          sx={{
            mt: 0.5,
            p: 1.5,
            maxWidth: '80%',
            backgroundColor: theme.palette.grey[50],
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 1.5,
          }}
        >
          <Typography variant="caption" sx={{ color: theme.palette.grey[700], fontStyle: 'normal' }}>
            What did you like?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {POSITIVE_FEEDBACK_REASONS.map(reason => (
              <Chip
                key={reason.key}
                label={reason.label}
                size="small"
                variant="outlined"
                onClick={() => handleReasonToggle(reason.key)}
                sx={{
                  cursor: 'pointer',
                  borderColor: selectedReasons.includes(reason.key)
                    ? theme.palette.ai.main
                    : theme.palette.grey[300],
                  backgroundColor: selectedReasons.includes(reason.key)
                    ? theme.palette.ai.light
                    : 'transparent',
                  color: selectedReasons.includes(reason.key)
                    ? theme.palette.ai.dark
                    : theme.palette.grey[700],
                  fontWeight: selectedReasons.includes(reason.key) ? 500 : 400,
                  '&:hover': {
                    borderColor: theme.palette.ai.main,
                    backgroundColor: `${theme.palette.ai.light}40`,
                    color: theme.palette.ai.dark,
                  },
                }}
              />
            ))}
          </Box>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            size="small"
            variant="outlined"
            placeholder="Tell us more (optional)..."
            value={comment}
            onChange={e => setComment(e.target.value)}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.ai.main,
                },
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              size="small"
              variant="text"
              onClick={handleSubmit}
              disabled={!canSubmit}
              sx={{ color: theme.palette.ai.main, textTransform: 'none' }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Collapse>

      <Collapse in={vote === 'down' && !isSubmitted} timeout={200}>
        <Box
          sx={{
            mt: 0.5,
            p: 1.5,
            maxWidth: '80%',
            backgroundColor: theme.palette.grey[50],
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 1.5,
          }}
        >
          <Typography variant="caption" sx={{ color: theme.palette.grey[700], fontStyle: 'normal' }}>
            What went wrong?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {FEEDBACK_REASONS.map(reason => (
              <Chip
                key={reason.key}
                label={reason.label}
                size="small"
                variant="outlined"
                onClick={() => handleReasonToggle(reason.key)}
                sx={{
                  cursor: 'pointer',
                  borderColor: selectedReasons.includes(reason.key)
                    ? theme.palette.ai.main
                    : theme.palette.grey[300],
                  backgroundColor: selectedReasons.includes(reason.key)
                    ? theme.palette.ai.light
                    : 'transparent',
                  color: selectedReasons.includes(reason.key)
                    ? theme.palette.ai.dark
                    : theme.palette.grey[700],
                  fontWeight: selectedReasons.includes(reason.key) ? 500 : 400,
                  '&:hover': {
                    borderColor: theme.palette.ai.main,
                    backgroundColor: `${theme.palette.ai.light}40`,
                    color: theme.palette.ai.dark,
                  },
                }}
              />
            ))}
          </Box>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            size="small"
            variant="outlined"
            placeholder="Tell us more (optional)..."
            value={comment}
            onChange={e => setComment(e.target.value)}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.ai.main,
                },
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              size="small"
              variant="text"
              onClick={handleSubmit}
              disabled={!canSubmit}
              sx={{ color: theme.palette.ai.main, textTransform: 'none' }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Collapse>

      {showThanks && (
        <Typography variant="caption" sx={{ color: theme.palette.grey[500], mt: 0.5, fontStyle: 'normal' }}>
          Thanks for your feedback
        </Typography>
      )}
    </Box>
  )
}

export default MessageFeedback
